import type { OfflineDelivery } from '@kidshub/contracts';

/**
 * What actually happened to a command, said plainly.
 *
 * `SPEC.md` §18.1 gives every command type a delivery rule, and §18.2 requires
 * the interface to be honest about it rather than implying certainty. The
 * distinction that matters most: a `PAUSE` for an offline tablet is **dropped,
 * not queued**, because a pause that arrives three hours late stops a session
 * the parent never meant to stop. Added time is still correct whenever it
 * lands, so it waits.
 *
 * **The rule is not restated here.** The server returns `offlineDelivery` with
 * every response, so this maps the answer it gave to a sentence rather than
 * keeping a second copy of the table that would quietly diverge from §18.1. All
 * this file knows is that three outcomes read differently to a parent.
 *
 * There is no "sending…" state on purpose. A spinner that never resolves is how
 * an interface implies a command is on its way to a tablet that is not
 * listening; every outcome here is terminal and says what became of it.
 */

export interface CommandOutcome {
  accepted: boolean;
  deviceIsOnline: boolean;
  offlineDelivery: OfflineDelivery;
}

/**
 * The translation key for what became of a command.
 *
 * The offline case is answered first, and that ordering is the whole point. A
 * dropped `PAUSE` comes back as `accepted: false` — the server is telling us it
 * did not take it, and `offlineDelivery: 'DROP'` is telling us why. Reading the
 * refusal first turns the most informative answer in this file into a shrug:
 * "the server did not accept this command", when what the parent needs to know
 * is that the tablet is not listening and a late pause is not worth sending.
 *
 * A refusal while the device *is* online is a different thing, and only then is
 * there nothing more specific to say.
 */
export const toOutcomeKey = (outcome: CommandOutcome): string => {
  if (!outcome.deviceIsOnline) {
    return `commands.outcome.offline.${outcome.offlineDelivery}`;
  }

  return outcome.accepted ? 'commands.outcome.delivered' : 'commands.outcome.refused';
};

/**
 * Whether the outcome is one the parent should feel the weight of.
 *
 * A dropped command is the only one where the parent's intent did not survive,
 * so it is the only one drawn in the colour reserved for things that are
 * actually broken.
 */
export const isOutcomeLost = (outcome: CommandOutcome): boolean =>
  !outcome.accepted || (!outcome.deviceIsOnline && outcome.offlineDelivery === 'DROP');
